import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { PageHeader, DataTable } from '../../components/common';
import { adminApi } from '../../utils/api';
import { format } from 'date-fns';

const Users = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.getUsers({
        search,
        page: page + 1,
        limit: pageSize,
        role: roleFilter !== 'all' ? roleFilter : undefined,
      });
      setUsers(data.users || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      // Show empty state on error - no mock data
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Used in DataTable row actions
  // eslint-disable-next-line no-unused-vars
  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminApi.updateUserRole(userId, newRole);
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user role:', err);
    }
  };

  const columns = [
    {
      field: 'email',
      headerName: 'User',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: params.row.user_role === 'admin' 
                ? theme.palette.primary.main 
                : theme.palette.text.secondary,
              fontSize: '0.85rem',
              flexShrink: 0,
            }}
          >
            {params.row.email?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              {params.row.email}
            </Box>
            {params.row.disabled && (
              <Chip
                label="Disabled"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  backgroundColor: `${theme.palette.error.main}20`,
                  color: theme.palette.error.main,
                }}
              />
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'user_role',
      headerName: 'Role',
      width: 120,
      renderCell: (params) => (
        <Chip
          icon={params.row.user_role === 'admin' ? <AdminIcon /> : <UserIcon />}
          label={params.row.user_role}
          size="small"
          sx={{
            backgroundColor:
              params.row.user_role === 'admin'
                ? `${theme.palette.primary.main}20`
                : `${theme.palette.text.secondary}15`,
            color:
              params.row.user_role === 'admin'
                ? theme.palette.primary.main
                : theme.palette.text.primary,
            fontWeight: 500,
            '& .MuiChip-icon': {
              fontSize: 16,
              color: 'inherit',
            },
          }}
        />
      ),
    },
    {
      field: 'recipes_count',
      headerName: 'Recipes',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.row.recipes_count || 0}
          size="small"
          variant="outlined"
          sx={{ minWidth: 50 }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Joined',
      width: 140,
      renderCell: (params) =>
        params.row.created_at
          ? format(new Date(params.row.created_at), 'MMM d, yyyy')
          : '-',
    },
    {
      field: 'last_sign_in_at',
      headerName: 'Last Active',
      width: 140,
      renderCell: (params) =>
        params.row.last_sign_in_at
          ? format(new Date(params.row.last_sign_in_at), 'MMM d, yyyy')
          : 'Never',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.disabled ? 'Enable user' : 'Disable user'}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              // Toggle disabled status
            }}
            sx={{
              color: params.row.disabled
                ? theme.palette.success.main
                : theme.palette.text.secondary,
            }}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="users"
        subtitle={`${totalCount} total users`}
        breadcrumbs={[{ label: 'users' }]}
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip title="Filter by role">
          <IconButton onClick={(e) => setFilterAnchor(e.currentTarget)}>
            <FilterIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={() => setFilterAnchor(null)}
        >
          <MenuItem
            selected={roleFilter === 'all'}
            onClick={() => {
              setRoleFilter('all');
              setFilterAnchor(null);
            }}
          >
            All Roles
          </MenuItem>
          <MenuItem
            selected={roleFilter === 'admin'}
            onClick={() => {
              setRoleFilter('admin');
              setFilterAnchor(null);
            }}
          >
            Admins Only
          </MenuItem>
          <MenuItem
            selected={roleFilter === 'user'}
            onClick={() => {
              setRoleFilter('user');
              setFilterAnchor(null);
            }}
          >
            Users Only
          </MenuItem>
        </Menu>
      </Box>

      {/* Data Table */}
      <DataTable
        rows={users}
        columns={columns}
        loading={loading}
        page={page}
        pageSize={pageSize}
        rowCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={(row) => navigate(`/users/${row.id}`)}
        getRowId={(row) => row.id}
      />
    </Box>
  );
};

export default Users;
